/*
  SIZMARI Atelier — Artwork product page
  File: assets/sizmari-artwork.js

  - Gallery: one image at a time, thumbnails, "01 / 07" counter,
    arrow keys, swipe, pauses videos when switching.
  - Variants: option buttons -> real Shopify variant, price, availability,
    Add to cart state, URL ?variant=, variant image.
  - Add to cart via /cart/add.js with a plain form POST as fallback.
  - Long description "Read more".
  - Theme-editor safe: one instance per section, cleaned up on unload.
*/
(function () {
  'use strict';

  if (window.SizmariArtwork) return;

  var instances = new WeakMap();

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function Artwork(root) {
    this.root = root;
    this.controller = new AbortController();
    this.signal = this.controller.signal;
    this.labels = root.dataset;

    this.initGallery();
    this.initVariants();
    this.initForm();
    this.initDescription();
  }

  /* ---------------- Gallery ---------------- */
  Artwork.prototype.initGallery = function () {
    var self = this;
    var signal = this.signal;
    this.gallery = this.root.querySelector('[data-sa-gallery]');
    if (!this.gallery) return;

    this.slides = Array.prototype.slice.call(this.gallery.querySelectorAll('[data-sa-slide]'));
    this.thumbs = Array.prototype.slice.call(this.gallery.querySelectorAll('[data-sa-thumb]'));
    this.thumbList = this.gallery.querySelector('[data-sa-thumbs]');
    this.counter = this.gallery.querySelector('[data-sa-current]');
    this.index = Math.max(0, this.slides.findIndex(function (s) { return !s.hidden; }));

    if (this.slides.length < 2) return;

    this.thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { self.show(i); }, { signal: signal });
      thumb.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = (i + 1) % self.thumbs.length;
        if (event.key === 'ArrowLeft') next = (i - 1 + self.thumbs.length) % self.thumbs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = self.thumbs.length - 1;
        if (next !== null) {
          event.preventDefault();
          event.stopPropagation();
          self.show(next);
          self.thumbs[next].focus();
        }
      }, { signal: signal });
    });

    this.gallery.addEventListener('keydown', function (event) {
      if (event.target !== self.gallery) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); self.step(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.step(-1); }
    }, { signal: signal });

    // Swipe (touch only; vertical scrolling keeps working)
    var startX = 0;
    var startY = 0;
    var stage = this.gallery.querySelector('[data-sa-stage]');
    stage.addEventListener('touchstart', function (event) {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true, signal: signal });
    stage.addEventListener('touchend', function (event) {
      var dx = event.changedTouches[0].clientX - startX;
      var dy = event.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) self.step(dx < 0 ? 1 : -1);
    }, { passive: true, signal: signal });
  };

  Artwork.prototype.step = function (delta) {
    var total = this.slides.length;
    this.show((this.index + delta + total) % total);
  };

  Artwork.prototype.show = function (i) {
    if (!this.slides || !this.slides[i] || i === this.index && !this.slides[i].hidden) return;
    var self = this;

    this.slides.forEach(function (slide, n) {
      var active = n === i;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
      if (!active) self.pauseMedia(slide);
    });

    this.thumbs.forEach(function (thumb, n) {
      var active = n === i;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (this.counter) this.counter.textContent = pad(i + 1);
    this.index = i;
    this.revealThumb(i);
  };

  Artwork.prototype.showMedia = function (mediaId) {
    if (!mediaId || !this.slides) return;
    var i = this.slides.findIndex(function (s) { return s.dataset.mediaId === String(mediaId); });
    if (i > -1) this.show(i);
  };

  Artwork.prototype.revealThumb = function (i) {
    var thumb = this.thumbs[i];
    var list = this.thumbList;
    if (!thumb || !list) return;
    var item = thumb.parentElement;
    var left = item.offsetLeft;
    var right = left + item.offsetWidth;
    if (left < list.scrollLeft) list.scrollLeft = left - 4;
    else if (right > list.scrollLeft + list.clientWidth) list.scrollLeft = right - list.clientWidth + 4;
  };

  Artwork.prototype.pauseMedia = function (slide) {
    var video = slide.querySelector('video');
    if (video && !video.paused) video.pause();
    var frame = slide.querySelector('iframe');
    if (frame && frame.contentWindow) {
      try {
        frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        frame.contentWindow.postMessage('{"method":"pause"}', '*');
      } catch (e) { /* ignore */ }
    }
  };

  /* ---------------- Variants ---------------- */
  Artwork.prototype.initVariants = function () {
    var self = this;
    var data = this.root.querySelector('[data-sa-variants]');
    this.fieldsets = Array.prototype.slice.call(this.root.querySelectorAll('[data-sa-option]'));
    if (!data || !this.fieldsets.length) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (e) {
      this.variants = null;
      return;
    }

    this.idInput = this.root.querySelector('[data-sa-variant-id]');
    this.price = this.root.querySelector('[data-sa-price]');
    this.availability = this.root.querySelector('[data-sa-availability]');
    this.addButton = this.root.querySelector('[data-sa-add]');
    this.soldoutInquire = this.root.querySelector('[data-sa-soldout-inquire]');

    this.root.addEventListener('change', function (event) {
      if (event.target.matches('[data-sa-option-input]')) self.onOptionChange();
    }, { signal: this.signal });

    this.markUnavailable(this.selectedOptions());
  };

  Artwork.prototype.selectedOptions = function () {
    return this.fieldsets.map(function (fieldset) {
      var checked = fieldset.querySelector('[data-sa-option-input]:checked');
      return checked ? checked.value : null;
    });
  };

  Artwork.prototype.findVariant = function (options) {
    return this.variants.find(function (variant) {
      return variant.options.every(function (value, i) { return value === options[i]; });
    }) || null;
  };

  Artwork.prototype.onOptionChange = function () {
    var options = this.selectedOptions();
    var variant = this.findVariant(options);
    this.markUnavailable(options);
    this.applyVariant(variant);
  };

  // Strike through values that are sold out or don't exist given the other selections.
  Artwork.prototype.markUnavailable = function (options) {
    var self = this;
    this.fieldsets.forEach(function (fieldset, optionIndex) {
      var inputs = fieldset.querySelectorAll('[data-sa-option-input]');
      Array.prototype.forEach.call(inputs, function (input) {
        var test = options.slice();
        test[optionIndex] = input.value;
        var match = self.findVariant(test);
        input.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  };

  Artwork.prototype.applyVariant = function (variant) {
    var labels = this.labels;
    var available = !!(variant && variant.available);

    if (this.idInput) this.idInput.value = variant ? variant.id : '';
    if (this.price && variant) this.price.textContent = variant.price;
    if (this.availability) {
      this.availability.textContent = !variant
        ? labels.labelUnavailable
        : available ? labels.labelAvailable : labels.labelSoldout;
    }
    if (this.addButton) {
      this.addButton.disabled = !available;
      this.addButton.textContent = !variant
        ? labels.labelUnavailable
        : available ? labels.labelAdd : labels.labelSoldout;
    }
    if (this.soldoutInquire) this.soldoutInquire.hidden = available;

    var status = this.root.querySelector('[data-sa-status]');
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error');
    }

    if (variant) {
      if (variant.media) this.showMedia(variant.media);
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* ignore */ }
    }
  };

  /* ---------------- Add to cart ---------------- */
  Artwork.prototype.initForm = function () {
    var self = this;
    var form = this.root.querySelector('.sa-form');
    if (!form || !window.fetch || !window.FormData) return;

    form.addEventListener('submit', function (event) {
      var button = form.querySelector('[data-sa-add]');
      var status = form.querySelector('[data-sa-status]');
      var idInput = form.querySelector('[data-sa-variant-id]');
      if (!idInput || !idInput.value) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      if (button) {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
      }
      if (status) {
        status.textContent = '';
        status.classList.remove('is-error');
      }

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || self.labels.rootUrl || '/';
      if (root.slice(-1) !== '/') root += '/';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) throw new Error(body.description || body.message || 'Error');
            return body;
          });
        })
        .then(function () {
          return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          self.updateCartCount(cart.item_count);
          if (status) {
            status.textContent = self.labels.labelAdded + ' ';
            var link = document.createElement('a');
            link.href = self.labels.cartUrl;
            link.textContent = self.labels.labelViewCart + ' →';
            status.appendChild(link);
          }
          document.dispatchEvent(new CustomEvent('sizmari:cart-updated', { detail: { cart: cart } }));
        })
        .catch(function (error) {
          if (status) {
            status.textContent = error.message;
            status.classList.add('is-error');
          }
        })
        .finally(function () {
          if (button) {
            button.removeAttribute('aria-busy');
            button.disabled = false;
          }
        });
    }, { signal: this.signal });
  };

  Artwork.prototype.updateCartCount = function (count) {
    var item = this.root.querySelector('[data-sa-cart-item]');
    var number = this.root.querySelector('[data-sa-cart-count]');
    if (number) number.textContent = count;
    if (item) item.hidden = !count;
  };

  /* ---------------- Long description ---------------- */
  Artwork.prototype.initDescription = function () {
    var desc = this.root.querySelector('[data-sa-desc]');
    var button = this.root.querySelector('[data-sa-more]');
    if (!desc || !button) return;

    var limit = parseFloat(getComputedStyle(this.root).getPropertyValue('--sa-desc-max')) || 200;
    if (desc.scrollHeight <= limit + 40) return;

    desc.classList.add('is-clamped');
    button.hidden = false;
    button.addEventListener('click', function () {
      var expanded = button.getAttribute('aria-expanded') === 'true';
      desc.classList.toggle('is-clamped', expanded);
      button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      button.hidden = !expanded;
    }, { signal: this.signal });
  };

  Artwork.prototype.destroy = function () {
    this.controller.abort();
  };

  /* ---------------- Lifecycle ---------------- */
  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-sizmari-artwork]');
    Array.prototype.forEach.call(roots, function (root) {
      if (!instances.has(root)) instances.set(root, new Artwork(root));
    });
  }

  function destroy(scope) {
    var roots = (scope || document).querySelectorAll('[data-sizmari-artwork]');
    Array.prototype.forEach.call(roots, function (root) {
      var instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroy(event.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  window.SizmariArtwork = { init: init, destroy: destroy };
})();
